// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './PublicFormView.scss';
import FormModel from "../../../../nor/ui/types/FormModel";
import Form from "../../../../nor/ui/components/form/Form";
import FormAppClassName from "../../../../constants/FormAppClassName";
import {FormValue} from "../../../../nor/ui/types/FormValue";
import LogService from "../../../../nor/ts/LogService";
import { VoidCallback } from "../../../../nor/ts/interfaces/callbacks";
import FormService from "../../../../services/FormService";
import RepositoryEntry from "../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import FormDTO from "../../../../nor/ui/types/FormDTO";
import Loader from '../../../../nor/ui/components/loader/Loader';
import { FieldChangeCallback } from "../../../../nor/ui/components/fields/FieldProps";

const LOG = LogService.createLogger('PublicFormView');

export enum PublicFormViewComponentState {

    UNLOADED,
    LOADING,
    LOADED,
    SUBMITTING,
    SUBMITTED,
    LOAD_ERROR,
    SUBMIT_ERROR

}

export interface PublicFormViewProps {

    readonly id : string;

}

export interface PublicFormViewState {

    readonly componentState : PublicFormViewComponentState;
    readonly model          : FormModel;
    readonly value          : FormValue;

}

export class PublicFormView extends React.Component<PublicFormViewProps, PublicFormViewState> {

    private readonly _cancelCallback      : VoidCallback;
    private readonly _submitCallback      : VoidCallback;
    private readonly _changeCallback      : FieldChangeCallback<FormValue>;

    private _componentState : PublicFormViewComponentState;

    public constructor(props: PublicFormViewProps) {

        super(props);

        this._componentState = PublicFormViewComponentState.UNLOADED;

        this.state = {
            componentState: this._componentState,
            model: {
                title: '',
                items: []
            },
            value: {
            }
        };

        this._submitCallback   = this._onSubmit.bind(this);
        this._cancelCallback   = this._onCancel.bind(this);
        this._changeCallback   = this._onSetValue.bind(this);

    }

    public componentDidMount() {

        this._updateForm();

    }

    public componentWillUnmount() {

    }

    public render () {

        const {
            model,
            value
        } = this.state;

        const componentState = this._componentState;

        const isLoading      = componentState === PublicFormViewComponentState.LOADING || componentState === PublicFormViewComponentState.UNLOADED;
        const isSubmitting   = componentState === PublicFormViewComponentState.SUBMITTING;
        const isSubmitted    = componentState === PublicFormViewComponentState.SUBMITTED;

        const hasLoadError   = componentState === PublicFormViewComponentState.LOAD_ERROR;
        const hasSubmitError = componentState === PublicFormViewComponentState.SUBMIT_ERROR;

        // FIXME: Implement user configurable messages

        return (
            <div className={FormAppClassName.PUBLIC_FORM_VIEW}>

                {hasLoadError ? (
                    <p className={FormAppClassName.PUBLIC_FORM_VIEW+'-load-error'}>Could not load the form.</p>
                ) : (
                    <React.Fragment>

                        {isSubmitted ? (
                            <p className={FormAppClassName.PUBLIC_FORM_VIEW+'-submitted-text'}>Kiitos! Lomake on tallennettu onnistuneesti.</p>
                        ) : (
                            <React.Fragment>

                                {isLoading ? (
                                    <Loader className={FormAppClassName.PUBLIC_FORM_VIEW+'-load-loader'} />
                                ) : (

                                    <Form
                                        className={FormAppClassName.PUBLIC_FORM_VIEW+'-form'}
                                        model={model}
                                        value={value}
                                        change={this._changeCallback}
                                        submit={this._submitCallback}
                                    />

                                )}

                                {isSubmitting ? (
                                    <Loader className={FormAppClassName.PUBLIC_FORM_VIEW+'-submit-loader'}  />
                                ) : null}

                                {hasSubmitError ? (
                                    <p className={FormAppClassName.PUBLIC_FORM_VIEW+'-submit-error-message'}>Could not submit the form.</p>
                                ) : null}

                            </React.Fragment>
                        )}

                    </React.Fragment>
                )}

            </div>
        );

    }

    private _updateForm () {

        this._componentState = PublicFormViewComponentState.LOADING;
        this.setState( {componentState: this._componentState});

        FormService.getFormById(this.props.id).then((form : RepositoryEntry<FormDTO>) => {

            this._componentState = PublicFormViewComponentState.LOADED;
            this.setState( {
                model: form.data.model,
                value: form.data.value ?? {},
                componentState: this._componentState
            });

        }).catch(err => {
            LOG.error(`Load Error: `, err);
            this._componentState = PublicFormViewComponentState.LOAD_ERROR;
            this.setState( {componentState: this._componentState});
        });

    }

    private _onSetValue (value  : FormValue) {
        LOG.debug('_onSetValue: value: ', value);
        this.setState({value: value});
    }

    private _onSubmit () {

        if (this._componentState === PublicFormViewComponentState.UNLOADED) {
            LOG.warn(`Warning! Still unloaded`);
            return;
        }

        if (this._componentState === PublicFormViewComponentState.LOADING) {
            LOG.warn(`Warning! Still loading`);
            return;
        }

        if (this._componentState === PublicFormViewComponentState.SUBMITTING) {
            LOG.warn(`Warning! Already submitting`);
            return;
        }

        LOG.debug('_onSubmit');

        this._componentState = PublicFormViewComponentState.SUBMITTING;
        this.setState( {componentState: this._componentState});

        FormService.submitForm(this.props.id, this.state.value).then(() => {

            this._componentState = PublicFormViewComponentState.SUBMITTED;
            this.setState( {componentState: this._componentState});

        }).catch(err => {

            LOG.error(`Failed to submit form: `, err);

            this._componentState = PublicFormViewComponentState.SUBMIT_ERROR;
            this.setState( {componentState: this._componentState});

        });

    }

    private _onCancel () {
        LOG.debug('WOOT: on cancel');
    }

}

export default PublicFormView;
